import { z } from "zod";
import { ALBUM_GROUPS, CAPTURE_SOURCES } from "@/lib/enums";
import { checklistSummarySchema } from "@/schemas/checklist";

/** `company=` value for rows with no company. Parentheses never appear in a domain name. */
export const NO_COMPANY = "(none)";

const FILTER_TERM_MAX = 120;

/** The query string of `GET /api/projects/:id/photos`. */
export const photoListQuerySchema = z.object({
  album: z.string().min(1).optional(),
  group: z.enum(ALBUM_GROUPS).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  company: z.string().min(1).optional(),
  make: z.string().trim().min(1).max(FILTER_TERM_MAX).optional(),
  model: z.string().trim().min(1).max(FILTER_TERM_MAX).optional(),
  serial: z.string().trim().min(1).max(FILTER_TERM_MAX).optional(),
});

/** The URL state of a project workspace. Bad values fall back; they are only preferences. */
export const projectSearchSchema = z.object({
  tab: z.enum(["albums", "photos", "map", "checklist", "sharing"]).catch("albums"),
  album: z.string().optional().catch(undefined),
  group: z.enum(ALBUM_GROUPS).optional().catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  company: z.string().optional().catch(undefined),
  make: z.string().max(FILTER_TERM_MAX).optional().catch(undefined),
  model: z.string().max(FILTER_TERM_MAX).optional().catch(undefined),
  serial: z.string().max(FILTER_TERM_MAX).optional().catch(undefined),
  order: z.enum(["desc", "asc"]).catch("desc"),
  // The photo open in the viewer. In the URL so back closes it and a link opens it.
  photo: z.string().optional().catch(undefined),
});

/** How a row shows: a picture, a file with no picture, or a row whose file we don't have. */
const PHOTO_KINDS = ["image", "file", "missing"] as const;

const photoSchema = z.object({
  id: z.string(),
  albumId: z.string(),
  fileName: z.string(),
  mime: z.string().nullable(),
  sizeBytes: z.number().nullable(),
  company: z.string().nullable(),
  addedOn: z.string(),
  capturedAt: z.string(),
  capturedSource: z.enum(CAPTURE_SOURCES),
  lat: z.string().nullable(),
  lng: z.string().nullable(),
  device: z.string().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  kind: z.enum(PHOTO_KINDS),
});
export type Photo = z.infer<typeof photoSchema>;

const pageFields = {
  nextCursor: z.string().nullable(),
  prevCursor: z.string().nullable(),
  hasNext: z.boolean(),
  hasPrev: z.boolean(),
  total: z.number(),
};

export const photoResponseSchema = z.object({ data: photoSchema });

export const photoListResponseSchema = z.object({ data: z.array(photoSchema), ...pageFields });
export type PhotoListResponse = z.infer<typeof photoListResponseSchema>;

const projectRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  spNumber: z.string().nullable(),
  photoCount: z.number(),
  albumCount: z.number(),
  lastUpload: z.string().nullable(),
  coverPhotoId: z.string().nullable(),
  checklist: checklistSummarySchema.nullable(),
});
export type ProjectRow = z.infer<typeof projectRowSchema>;

export const projectListResponseSchema = z.object({
  data: z.array(projectRowSchema),
  ...pageFields,
});

const albumSchema = z.object({
  id: z.string(),
  name: z.string(),
  groupName: z.enum(ALBUM_GROUPS),
  isMc: z.boolean(),
  photoCount: z.number(),
  coverPhotoId: z.string().nullable(),
});
export type Album = z.infer<typeof albumSchema>;

const projectDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  spNumber: z.string().nullable(),
  photoCount: z.number(),
  missingCount: z.number(),
  albums: z.array(albumSchema),
  companies: z.array(z.string()),
  hasNoCompanyRows: z.boolean(),
  checklist: checklistSummarySchema.nullable(),
});
export type ProjectDetail = z.infer<typeof projectDetailSchema>;

export const projectDetailResponseSchema = z.object({ data: projectDetailSchema });
